import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import api, { setToken, setLoggingOut } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mfaPending, setMfaPending] = useState(null); // {user} awaiting email OTP

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      setProfile(data.profile);
      return data.user;
    } catch (e) {
      setUser(null);
      setProfile(null);
      return null;
    }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    (async () => {
      await refresh();
      setLoading(false);
    })();
  }, [refresh]);

  const login = async (email, password) => {
    setLoggingOut(false);
    const { data } = await api.post("/auth/login", { email, password });
    setToken(data.token);
    if (data.otp_required) { setMfaPending({ user: data.user, expiresAt: data.otp_expires_at, cooldownUntil: Date.now() + ((data.resend_cooldown || 30) * 1000) }); return { otp_required: true }; }
    setUser(data.user);
    await refresh();
    return data.user;
  };

  const register = async (payload) => {
    setLoggingOut(false);
    const { data } = await api.post("/auth/register", payload);
    setToken(data.token);
    setUser(data.user);
    await refresh();
    return data.user;
  };

  const completeGoogleSession = async (sessionId) => {
    setLoggingOut(false);
    const { data } = await api.post("/auth/session", { session_id: sessionId });
    setToken(data.token);
    if (data.otp_required) { setMfaPending({ user: data.user, expiresAt: data.otp_expires_at, cooldownUntil: Date.now() + ((data.resend_cooldown || 30) * 1000) }); return { otp_required: true }; }
    setUser(data.user);
    await refresh();
    return data.user;
  };

  const verifyOtp = async (code) => {
    await api.post("/auth/verify-otp", { code });
    setMfaPending(null);
    return await refresh();
  };

  const resendOtp = async () => {
    const { data } = await api.post("/auth/resend-otp");
    setMfaPending((m) => (m ? { ...m, expiresAt: data.otp_expires_at, cooldownUntil: Date.now() + ((data.resend_cooldown || 30) * 1000) } : m));
    return data;
  };

  const logout = async () => {
    // Enter teardown mode so any background/in-flight request that 401s is
    // treated as expected (no unhandled rejection / overlay).
    setLoggingOut(true);
    try { await api.post("/auth/logout"); } catch (e) {}
    setToken(null);
    setUser(null);
    setProfile(null);
    setMfaPending(null);
    // Keep the teardown guard active briefly to absorb any trailing in-flight
    // requests, then restore normal error handling.
    setTimeout(() => setLoggingOut(false), 2000);
  };

  return (
    <AuthContext.Provider value={{ user, profile, loading, mfaPending, login, register, logout, refresh, completeGoogleSession, verifyOtp, resendOtp, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
